/**
 * Zedaro Price Breaks
 * Reads costing + min_qty metafield data from the cart/product, prices each
 * quantity by the locked tier model, renders a banner, shows a below-minimum
 * warning and a "buy N to save" nudge, and replaces the checkout button with
 * "Checkout at Tier Prices". Re-runs after cart:refresh (mini-cart re-render).
 */

(function () {
  'use strict';

  function round2(n) { return Math.round(n * 100) / 100; }

  function formatMoney(amount) {
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ── Shared tier-price model — MUST match backend _tier_price_for ────────────
  // Rules (locked): the costing tiers are the only prices; the first (lowest-qty)
  // tier is the highest per-unit price and the effective base. min_qty (default
  // 1) is the floor: below it the quantity is invalid. Tiers below min_qty are
  // discarded (min_qty wins), so the lowest remaining tier is the effective
  // first tier; any valid quantity at or below the first breakpoint takes that
  // first-tier price, and higher quantities take the highest tier whose qty is
  // <= the quantity. No "variant price" concept exists here — the tiers are it.
  function priceModel(tiers, minQty, qty) {
    var mq = (typeof minQty === 'number' && minQty >= 1) ? Math.floor(minQty) : 1;
    var asc = (tiers || []).slice().sort(function (a, b) { return a.qty - b.qty; });

    if (asc.length === 0) {
      // No tiers: min_qty is the only rule; no tier price / no volume message.
      return { valid: qty >= mq, price: null, floor: mq, hasTiers: false,
               belowMin: qty < mq, firstTierPrice: null, nextTier: null };
    }
    if (qty < mq) {
      return { valid: false, price: null, floor: mq, hasTiers: true,
               belowMin: true, firstTierPrice: null, nextTier: null };
    }

    var orderable = asc.filter(function (t) { return t.qty >= mq; });
    var effFirst  = orderable.length ? orderable[0] : asc[asc.length - 1];
    var eff       = asc.filter(function (t) { return t.qty >= effFirst.qty; });

    var price = effFirst.usd;
    for (var i = 0; i < eff.length; i++) { if (qty >= eff[i].qty) price = eff[i].usd; }

    var next = null;
    for (var j = 0; j < eff.length; j++) {
      if (eff[j].qty > qty && eff[j].usd < price) { next = eff[j]; break; }
    }

    return { valid: true, price: price, floor: mq, hasTiers: true, belowMin: false,
             firstTierPrice: effFirst.usd,
             nextTier: next ? { qty: next.qty, usd: next.usd } : null };
  }

  function lineMinQty(item) {
    var m = parseInt(item.min_qty, 10);
    return (isFinite(m) && m >= 1) ? m : 1;
  }

  // ── Cart analysis ───────────────────────────────────────────────────────────
  // Categorises each cart line: below-minimum (hard warning), or a saving vs the
  // first tier. The checkout button is offered for the saving lines.
  function analyzeLines(lineItems) {
    // The cart shows Shopify's LIST total, because Shopify charges the variant
    // price and tier pricing is only applied when the draft order is built. The
    // banner therefore has to state what the customer will actually pay, not
    // just a saving floating beside a total that contradicts it — a "save
    // $307.76" line next to an unchanged $8,422.92 reads as nonsense.
    //
    // `effective` mirrors _build_line_item exactly: a tier is applied only when
    // it is BELOW the variant price, never above it. So the figure shown here is
    // the figure the invoice will carry.
    var belowMin = [], savings = [];
    var listTotal = 0, tierTotal = 0, plainLines = 0;

    for (var i = 0; i < lineItems.length; i++) {
      var item    = lineItems[i];
      var costing = item.costing;
      var tiers   = (costing && costing.sell_zd) || [];
      var qty     = item.quantity;
      var listEa  = item.originalPrice;
      var m       = priceModel(tiers, lineMinQty(item), qty);

      listTotal += listEa * qty;

      if (m.belowMin) {
        belowMin.push({ title: item.title, quantity: qty, minQty: m.floor });
        tierTotal += listEa * qty;      // nothing will ship at a tier price
        continue;
      }

      var effective = (m.hasTiers && m.price != null && m.price < listEa)
                    ? m.price : listEa;
      tierTotal += effective * qty;

      if (effective < listEa) {
        savings.push({
          title: item.title, quantity: qty,
          tierPrice: effective,
          lineTotal: round2(effective * qty),
          saving: round2((listEa - effective) * qty)
        });
      } else {
        // Counted, not listed. Naming every unaffected item would make the
        // sidebar unusable on a large cart; one line covers them.
        plainLines += 1;
      }
    }

    return {
      belowMin: belowMin,
      savings: savings,
      plainLines: plainLines,
      listTotal: round2(listTotal),
      tierTotal: round2(tierTotal),
      totalSaving: round2(listTotal - tierTotal)
    };
  }

  // ── Banner rendering (DOM nodes, never innerHTML) ───────────────────────────
  // ZOPS-072: product titles used to be concatenated into an HTML string that was
  // assigned to container.innerHTML, so a title containing markup or an event
  // attribute became executable DOM in the storefront origin. Titles are untrusted
  // catalogue data. Everything below builds elements and sets text with
  // textContent, which cannot create elements or handlers no matter what the title
  // contains. Do not reintroduce innerHTML here — no markup is needed.

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function renderBanner(container, analysis) {
    container.textContent = '';                      // clear without parsing HTML
    var banner = el('div', 'zedaro-pb__banner');

    if (analysis.belowMin.length) {
      var warn = el('div', 'zedaro-pb__warn');
      warn.appendChild(el('div', 'zedaro-pb__heading zedaro-pb__heading--warn',
                          '\u26a0 Minimum Order Quantity'));
      analysis.belowMin.forEach(function (l) {
        var row = el('div', 'zedaro-pb__row--warn');
        row.appendChild(el('span', 'zedaro-pb__item-title', l.title));
        row.appendChild(el('span', 'zedaro-pb__item-min',
          'Minimum order ' + l.minQty + ' \u2014 you have ' + l.quantity +
          '. Please increase the quantity.'));
        warn.appendChild(row);
      });
      banner.appendChild(warn);
    }

    if (analysis.savings.length) {
      // "Volume pricing available" read as an upsell — buy more and you COULD
      // save — when what it actually means is that the price has already been
      // earned and the total above the banner is not what will be charged.
      banner.appendChild(el('div', 'zedaro-pb__heading', '\u2713 Your Tier Pricing'));

      analysis.savings.forEach(function (l) {
        var row = el('div', 'zedaro-pb__row');
        row.appendChild(el('span', 'zedaro-pb__item-title',
                           l.title + ' \u00d7 ' + l.quantity));
        row.appendChild(el('span', 'zedaro-pb__item-saving',
          formatMoney(l.tierPrice) + ' ea \u00b7 ' + formatMoney(l.lineTotal) +
          ' (saving ' + formatMoney(l.saving) + ')'));
        banner.appendChild(row);
      });

      if (analysis.plainLines > 0) {
        banner.appendChild(el('div', 'zedaro-pb__row--plain',
                              'All other items are priced normally.'));
      }

      // The number that resolves the contradiction with the cart total.
      var total = el('div', 'zedaro-pb__total', 'Cart Total at Tier Prices: ');
      total.appendChild(el('strong', null, formatMoney(analysis.tierTotal)));
      banner.appendChild(total);
      banner.appendChild(el('div', 'zedaro-pb__total-note',
        'You save ' + formatMoney(analysis.totalSaving) + '. The total shown ' +
        'above is the list total \u2014 "Checkout at Tier Prices" applies your ' +
        'volume pricing.'));
    }

    container.appendChild(banner);
  }

  // ── Tier checkout ───────────────────────────────────────────────────────────
  // ZOPS-048. The old code posted straight from the browser to the zOps public
  // domain, sending only the lines that carried a saving. It now posts the
  // COMPLETE cart to the App Proxy, which Shopify signs; zops-admin establishes
  // the customer from that signed request and calls the private backend.
  //
  // The native checkout button is kept in memory and restored on ANY failure, so
  // a customer can never be left with a dead button and no way to pay. That was
  // the state the store was in while the public backend domain was switched off.

  // ONE asset, TWO themes (2026-08-10). Warehouse is live; Trade (Dawn) is
  // being brought to parity. Selector lists, first match wins, so the same
  // file serves both and there is no second copy to drift — the exact defect
  // the carrier list had. Warehouse selectors stay listed even after a
  // migration to Trade, because a preview theme and a live theme swap roles
  // without warning.
  function firstMatch(selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var found = document.querySelector(selectors[i]);
      if (found) return found;
    }
    return null;
  }

  function findCheckoutButton(context) {
    return context === 'mini-cart'
      ? firstMatch(['#CartDrawer-Checkout',
                    '.mini-cart__button-container button[name="checkout"]'])
      : firstMatch(['#checkout',
                    '.cart-recap__checkout']);
  }

  function nativeCheckoutDisabled(context) {
    // True when the cart's own checkout button is disabled — for below-minimum
    // lines or any other reason the theme decided (audit ZOPS-126). Treated as
    // authoritative: if the cart says checkout is not allowed, the tier button
    // must not offer one.
    var btn = findCheckoutButton(context);
    if (!btn) return false;
    return !!btn.disabled || btn.getAttribute('aria-disabled') === 'true';
  }

  function replaceCheckoutButton(context, data) {
    var original = findCheckoutButton(context);
    if (!original) return;
    if (original.dataset.zedaroReplaced) return;
    original.dataset.zedaroReplaced = 'true';

    var newBtn = document.createElement('button');
    newBtn.type = 'button';
    newBtn.className = original.className;
    newBtn.textContent = 'Checkout at Tier Prices';

    function restoreNative(message) {
      // Put the real button back before telling the customer anything, so the
      // page is never in a state where checkout is impossible.
      if (newBtn.parentNode) newBtn.parentNode.replaceChild(original, newBtn);
      delete original.dataset.zedaroReplaced;
      if (message) {
        var notice = document.getElementById('zedaro-pb-checkout-notice');
        if (!notice) {
          notice = el('div', 'zedaro-pb__warn');
          notice.id = 'zedaro-pb-checkout-notice';
          original.parentNode.insertBefore(notice, original);
        }
        notice.textContent = message;
      }
    }

    newBtn.addEventListener('click', function () {
      newBtn.disabled = true;
      newBtn.textContent = 'Creating Order\u2026';
      tierCheckout(data, restoreNative);
    });

    original.parentNode.replaceChild(newBtn, original);
  }

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  function describeFailure(reason, lines) {
    if (reason === 'signin_required') {
      return 'Please sign in to check out at tier prices.';
    }
    // Zedaro sells to approved accounts only. The public may browse listed
    // parts so they can apply; browsing is not buying (ZOPS-076). Say so
    // plainly rather than falling through to "try the standard button",
    // which sends someone to a checkout that will also refuse them.
    if (reason === 'not_approved') {
      return 'Your account is not yet approved for online ordering. '
           + 'Please contact us to complete your account setup.';
    }
    if (reason === 'forbidden') {
      return 'One or more items in your cart are not available on your '
           + 'account. Please remove them, or contact us for access.';
    }
    // ZOPS-077 round 2: two non-error outcomes from the idempotency layer.
    // in_progress means a double-click's first click is still working;
    // unavailable means Shopify could not be read while reconciling, and
    // nothing was created — retrying shortly is safe and correct.
    if (reason === 'in_progress') {
      return 'Your checkout is already being prepared — one moment, then '
           + 'press Checkout again to pick it up.';
    }
    if (reason === 'unavailable') {
      return 'Checkout is momentarily unavailable. Nothing was created — '
           + 'please try again in a few seconds.';
    }
    if (reason === 'below_minimum' && lines && lines.length) {
      return 'Below minimum order quantity: ' + lines.map(function (l) {
        return l.title + ' (' + l.qty + ' of ' + l.min_qty + ')';
      }).join('; ') + '. Please increase the quantity.';
    }
    return 'Tier checkout is unavailable right now — use the standard checkout '
         + 'button and we will apply your volume pricing before invoicing.';
  }

  function tierCheckout(data, restoreNative) {
    readCart().then(function (cart) {
      if (!cart || !cart.items || cart.items.length === 0) {
        restoreNative(describeFailure('bad_cart'));
        return;
      }
      // EVERY line, not just the ones with a saving. A line with no costing
      // simply gets no discount server-side; it must not vanish from the order.
      var items = cart.items.map(function (i) {
        return { variant_id: String(i.variant_id), quantity: i.quantity };
      });

      return fetch(data.proxyPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (resp) {
        return resp.json().catch(function () { return null; }).then(function (j) {
          if (!resp.ok || !j || !j.ok || !j.invoice_url) {
            restoreNative(describeFailure(j && j.reason, j && j.lines));
            return;
          }
          // The invoice checkout is a separate order from the cart. Clearing the
          // cart stops the customer coming back and buying the same items again
          // at list price.
          return fetch('/cart/clear.js', { method: 'POST' })
            .catch(function () { /* a stale cart is better than no redirect */ })
            .then(function () { window.location.href = j.invoice_url; });
        });
      });
    }).catch(function (err) {
      console.error('zOps tier checkout error:', err);
      restoreNative(describeFailure(null));
    });
  }

  function init() {
    var dataEl = document.getElementById('zedaro-price-breaks-data');
    var container = document.getElementById('zedaro-price-breaks-container');
    if (!dataEl || !container) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var analysis = analyzeLines(data.lineItems);
    if (analysis.belowMin.length === 0 && analysis.savings.length === 0) return;

    var context = container.dataset.context || 'cart';
    renderBanner(container, analysis);
    // NEVER replace a checkout the cart has disabled (audit ZOPS-126). A mixed
    // cart — one line below its canonical minimum, another earning tier
    // savings — had the native (correctly disabled) button swapped for a fresh
    // ENABLED tier button, re-opening a checkout the MOQ rule had just closed.
    // The savings/MOQ banner still shows; only the button is withheld until the
    // cart is actually valid. Both conditions must hold, and the native button
    // must not be disabled for any OTHER reason either.
    if (analysis.savings.length && analysis.belowMin.length === 0
        && !nativeCheckoutDisabled(context)) {
      replaceCheckoutButton(context, data);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    init();
    initProductPage();
  });

  document.addEventListener('cart:refresh', function () {
    setTimeout(init, 100);
  });

  if (window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].id === 'zedaro-price-breaks-data' ||
                (added[j].querySelector && added[j].querySelector('#zedaro-price-breaks-data'))) {
              init();
              return;
            }
            // Dawn replaces the whole price container on variant change, so
            // the elements the product-page bindings hold go stale. Re-bind
            // when a costing node reappears. (Warehouse never re-renders the
            // price container, so this path simply never fires there.)
            if (added[j].querySelector &&
                (added[j].hasAttribute && added[j].hasAttribute('data-zedaro-costing') ||
                 added[j].querySelector('[data-zedaro-costing]'))) {
              initProductPage();
              return;
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Product page: dynamic price + messages on quantity change ───────────────

  function initProductPage() {
    var priceList = document.querySelector('.price-list[data-zedaro-costing]');
    if (!priceList) return;

    var costingRaw = priceList.getAttribute('data-zedaro-costing');
    if (!costingRaw) return;

    var costing;
    try { costing = JSON.parse(costingRaw); } catch (e) { return; }

    var tiers = costing.sell_zd;
    if (!tiers || tiers.length === 0) return;

    var minQty = parseInt(priceList.getAttribute('data-zedaro-min-qty'), 10);
    if (!isFinite(minQty) || minQty < 1) minQty = 1;

    var priceEl  = document.getElementById('zedaro-product-price');
    var noticeEl = document.getElementById('zedaro-tier-notice');
    var qtyInput = firstMatch(['.quantity-selector--product [name="quantity"]',
                               '.product-form__input--quantity input[name="quantity"]',
                               'quantity-input input[name="quantity"]']);
    if (!priceEl || !qtyInput) return;

    function setNotice(text, warn) {
      if (!noticeEl) return;
      if (!text) { noticeEl.style.display = 'none'; return; }
      noticeEl.textContent = text;
      noticeEl.style.color = warn ? '#c0392b' : '#2e7d32';
      noticeEl.style.display = 'block';
    }

    function updatePrice() {
      var qty = parseInt(qtyInput.value, 10) || 1;
      var m = priceModel(tiers, minQty, qty);

      if (m.belowMin) {
        // Below the true minimum orderable quantity — invalid.
        setNotice('Minimum Order Quantity: ' + m.floor, true);
        priceEl.style.color = '';
        return;
      }
      if (m.price == null) { setNotice('', false); priceEl.style.color = ''; return; }

      priceEl.textContent = formatMoney(m.price);
      priceEl.style.color = (m.price < m.firstTierPrice) ? '#2e7d32' : '';

      if (m.nextTier) {
        // Valid, but a higher quantity is cheaper per unit — soft nudge.
        setNotice('Buy ' + m.nextTier.qty + ' for ' + formatMoney(m.nextTier.usd) + ' ea', false);
      } else if (m.price < m.firstTierPrice) {
        setNotice('Volume price applied for qty ' + qty, false);
      } else {
        setNotice('', false);
      }
    }

    // The +/- steppers live in a wrapper whose tag differs per theme:
    // Warehouse a div.quantity-selector--product, Dawn a <quantity-input>
    // custom element. The input's own events are bound either way, so a theme
    // with neither wrapper still updates on typed changes.
    var qtyWrapper = qtyInput.closest('.quantity-selector--product') ||
                     qtyInput.closest('quantity-input');
    if (qtyWrapper) {
      qtyWrapper.addEventListener('click', function () { setTimeout(updatePrice, 10); });
    }
    qtyInput.addEventListener('input', updatePrice);
    qtyInput.addEventListener('change', updatePrice);
    qtyInput.addEventListener('keyup', updatePrice);
    updatePrice(); // set initial state on load
  }

})();
